import {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ApiError, OperationKey, browserConfirmEmail, getMe, type BrowserTokens} from "../../../shared/api/client";
import {localizeAPIError} from "../../../shared/i18n/api-errors";
import {sessionStore} from "../../../shared/session/session-store";
import {PhoneVerification} from "./phone-verification";

export function EmailConfirm() {
  const {t} = useTranslation();
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const operation = useRef(new OperationKey());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [phoneVerification, setPhoneVerification] = useState<{phone: string; tokens: BrowserTokens} | null>(null);
  const localizedError = error ? localizeAPIError(error, "email-confirm", t) : null;

  const confirm = async () => {
    if (!token) {
      setError(new ApiError(422, errorEnvelope("missing_confirmation_token")));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const tokens = await browserConfirmEmail(token, operation.current);
      const me = await getMe(tokens.access_token);
      if (me.phone && !me.phone.verified) {
        setPhoneVerification({phone: me.phone.value, tokens});
        return;
      }
      await sessionStore.accept(tokens);
      window.location.assign("/app/");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(0, errorEnvelope("client_error")));
    } finally {
      setBusy(false);
    }
  };

  if (phoneVerification) {
    return <PhoneVerification
      phone={phoneVerification.phone}
      tokens={phoneVerification.tokens}
      onVerified={async () => {
        await sessionStore.accept(phoneVerification.tokens);
        window.location.assign("/app/");
      }}
    />;
  }

  return <section className="auth-card">
    <h1>{t("auth.emailConfirmTitle")}</h1>
    <p>{t("auth.emailConfirmBody")}</p>
    {localizedError && <div className="form-error" role="alert">
      <p>{localizedError.message}</p>
      {localizedError.requestId && <small>{t("common.requestId")}: <code>{localizedError.requestId}</code></small>}
    </div>}
    <button className="button primary" type="button" disabled={busy} aria-busy={busy} onClick={confirm}>{busy ? t("auth.confirming") : t("auth.confirmEmail")}</button>
  </section>;
}

function errorEnvelope(code: string) {
  return {error: {code, message: "", request_id: "", fields: {}}};
}
